import SidebarItem from "@/components/sidebar/SidebarItem.tsx";
import React, { useCallback, useRef, useState } from "react";
import { useAuthenticationContext } from "../store/authContext/authContext";
import { useCurrentWorkspace } from "@/store/authContext/authContext";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronLeft, ChevronRight, Lightbulb } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import SidebarChatSection from "./sidebar/SidebarChatSection";
import SidebarAdvancedReportSection from "./sidebar/SidebarAdvancedReportSection";

import SidebarConsoleSection from "./sidebar/SidebarConsoleSection";
import { Link, useLocation, useMatch, useNavigate } from "react-router-dom";
import SidebarAccountSection from "./sidebar/SidebarAccountSection";
import SidebarWorkspaceSection from "./sidebar/SidebarWorkspaceSection";
import MiniSidebarAccountSection from "./sidebar/MiniSidebarAccountSection";
import SidebarDecksButton from "./sidebar/SidebarDecksButton";
import SidebarPortfolioDashboardSection from "./sidebar/SidebarPortfolioDashboardSection";
import FLAGS from "@/constants/flags.ts";
import mixpanel from "@/lib/mixpanel";

const paritaLogo = new URL(
    "../assets/PeopleScapeAI_LogoBadge_RGB.png",
    import.meta.url,
  ).toString(),
  paritaLogoSm = new URL(
    "../assets/peoplescape-penguin-sm.png",
    import.meta.url,
  ).toString();

interface SidebarProps {
  isExpanded: boolean;
  onExpandChange: (isExpanded: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isExpanded, onExpandChange }) => {
  const { state: authContext, dispatch } = useAuthenticationContext();
  const location = useLocation();
  const matchCurrentReport = useMatch("/report/:id");
  const matchNewReport = useMatch("/new-report");
  const matchCurrentWorkspace = useMatch("/workspace/:id");
  const matchNewWorkspace = useMatch("/new-workspace");
  const workspace = useCurrentWorkspace();
  const initialExpandedGroups: Set<string> = new Set(
    authContext.userData?.sidebar_settings.expandedGroups,
  );
  if (matchCurrentReport || matchNewReport) {
    initialExpandedGroups.add("reports");
  }
  if (matchCurrentWorkspace || matchNewWorkspace) {
    initialExpandedGroups.add("workspaces");
  }

  const [expandedGroups, setExpandedGroups] = useState<string[]>(
    Array.from(initialExpandedGroups),
  );
  const sidebarRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const workspaces = [
    { id: "1", title: "Attrition", path: "/workspace/attrition" },
    { id: "2", title: "Recruitment", path: "/workspace/recruitment" },
    { id: "3", title: "Culture", path: "/workspace/culture" },
  ];

  const handleGroupExpand = useCallback(
    (groupId: string, value: boolean) => {
      setExpandedGroups((prev) => {
        const newExpandedGroups = value
          ? [...prev, groupId]
          : prev.filter((id) => id !== groupId);
        // debugger;
        dispatch({
          type: "updateUserSidebarSettingsAction",
          payload: {
            expandedGroups: newExpandedGroups,
            isPinned: isExpanded,
          },
        });
        return newExpandedGroups;
      });
    },
    [isExpanded, dispatch],
  );

  const handleExpandClick = useCallback(() => {
    const newExpandedState = !isExpanded;
    onExpandChange(newExpandedState);
    dispatch({
      type: "updateUserSidebarSettingsAction",
      payload: { expandedGroups, isPinned: newExpandedState },
    });
  }, [isExpanded, onExpandChange, expandedGroups, dispatch]);

  const getActiveSections = () => {
    const path = location.pathname;
    return {
      console: !path || path === "/",
      chats: path.startsWith("/chat/") || path === "/new-chat",
      dashboards: path.startsWith("/dashboard/"),
      workspaces: path.startsWith("/workspace/"),
      reports: path.startsWith("/report/") || path === "/new-report",
      data: path.startsWith("/data/"),
    };
  };

  const activeSections = getActiveSections();

  return (
    <div
      ref={sidebarRef}
      className={`fixed left-0 h-full bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-[width] duration-300 z-50 ${
        isExpanded ? "w-64" : "w-14"
      }`}
    >
      <ScrollArea className="h-full [&>div>div]:!block">
        <div className={`flex flex-col h-full ${isExpanded ? "p-3" : "p-1"}`}>
          {isExpanded ? (
            <div className="mb-8 m-auto mt-5">
              <img src={paritaLogo} alt="Parita Logo" className="" />
            </div>
          ) : (
            <div className="mb-8 m-auto mt-4">
              <img src={paritaLogoSm} alt="Parita Logo" className="h-8 w-8" />
            </div>
          )}

          <div className="flex-1 space-y-3">
            <SidebarConsoleSection
              onClick={() => navigate("/")}
              isActive={activeSections.console}
              isExpanded={isExpanded}
              isMiniMode={!isExpanded}
            />
            {workspace.customer_reports_active && workspace.is_admin && (
              <SidebarPortfolioDashboardSection isMiniMode={!isExpanded} />
            )}
            <SidebarChatSection
              isExpanded={isExpanded && expandedGroups.includes("chats")}
              onToggle={(value) => handleGroupExpand("chats", value)}
              onModalStateChange={() => {}}
              isActive={activeSections.chats}
              isMiniMode={!isExpanded}
            />
            <SidebarAdvancedReportSection
              isExpanded={
                isExpanded && expandedGroups.includes("advanced-reports")
              }
              onToggle={(value) => handleGroupExpand("advanced-reports", value)}
              onModalStateChange={() => {}}
              isActive={activeSections.reports}
              isMiniMode={!isExpanded}
            />
            {FLAGS.DECK_BUILDER && (
              <Link
                to="/decks"
                className="block"
                onClick={() =>
                  mixpanel.track("Click", { eventDescription: "Deck Builder" })
                }
              >
                <SidebarDecksButton isMiniMode={!isExpanded} />
              </Link>
            )}
            <SidebarWorkspaceSection
              workspaces={workspaces}
              isExpanded={isExpanded && expandedGroups.includes("workspaces")}
              onToggle={(value) => handleGroupExpand("workspaces", value)}
              onModalStateChange={() => {}}
              isActive={activeSections.workspaces}
              isMiniMode={!isExpanded}
            />
            {FLAGS.DEEP_INSIGHTS && authContext.deepInsightsState.enabled && (
              <Link
                to="/deep-insights"
                className="block"
                onClick={() =>
                  mixpanel.track("Click", {
                    eventDescription: "Deep Insights",
                  })
                }
              >
                <SidebarItem
                  hideChevron={true}
                  icon={
                    <Lightbulb size={20} color="var(--sidebar-item-color)" />
                  }
                  label="Deep Insights"
                  isExpanded={false}
                  onToggle={() => {}}
                  isActive={false}
                />
              </Link>
            )}
          </div>

          <div className="mt-auto">
            {isExpanded ? (
              <>
                <SidebarAccountSection />
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="float-right mt-2 hover:bg-transparent"
                        onClick={handleExpandClick}
                      >
                        <ChevronLeft className="text-gray-400" size={20} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Collapse Sidebar</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            ) : (
              <>
                <MiniSidebarAccountSection />
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="float-right mt-2 hover:bg-transparent"
                        onClick={handleExpandClick}
                      >
                        <ChevronRight className="text-gray-400" size={20} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Expand Sidebar</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            )}
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};

export default Sidebar;
